import * as React from 'react';

import { getOnboardingSwipeOffset, getOnboardingTimeline } from './utils';
import { useOnboardingSwipeAnimationConfig } from './useOnboardingSwipeAnimationConfig';
import { useReplyOnboardingContext } from '../../utils/replyOnboardingContext';

interface IUseMessageSwipeOnboardingParams {
  messageRef: React.RefObject<HTMLElement | null>;
  contentElementRef: React.RefObject<HTMLElement | null>;
  iconElementRef: React.RefObject<HTMLElement | null>;
  hintElementRef: React.RefObject<HTMLElement | null>;
  isSwipeOnboardingTarget: boolean;
}

interface IUseMessageSwipeOnboardingResult {
  /** Идёт анимация онбординга — жест пользователя нужно выключить */
  isOnboardingAnimationPlaying: boolean;
}

/** Демо свайпа-реплая: сдвиг контента + opacity иконки и подсказки. Пока играет — жест выключен. */
export const useMessageSwipeOnboarding = ({
  messageRef,
  contentElementRef,
  iconElementRef,
  hintElementRef,
  isSwipeOnboardingTarget,
}: IUseMessageSwipeOnboardingParams): IUseMessageSwipeOnboardingResult => {
  const { complete, onOnboardingShown } = useReplyOnboardingContext();

  const { startDelayMs, slideMs, holdMs, textFadeMs } = useOnboardingSwipeAnimationConfig();

  const [isOnboardingAnimationPlaying, setIsOnboardingAnimationPlaying] = React.useState(false);

  React.useEffect(() => {
    const contentElement = contentElementRef.current;
    const messageElement = messageRef.current;

    if (!isSwipeOnboardingTarget || !contentElement || !messageElement) {
      return;
    }

    const { total, slideEnd, hintVisible, hintHoldEnd, slideBackStart } = getOnboardingTimeline({
      slideMs,
      holdMs,
      fadeMs: textFadeMs,
    });

    // Подтягиваем сообщение в зону видимости (в длинных чатах оно может быть вне вьюпорта)
    messageElement.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });

    setIsOnboardingAnimationPlaying(true);

    onOnboardingShown();

    const swipeOffset = getOnboardingSwipeOffset(messageElement);
    const timing = { duration: total, delay: startDelayMs };
    const animations: Animation[] = [];

    const contentAnimation = contentElement.animate(
      [
        { transform: 'translateX(0)', easing: 'ease-in-out' },
        { transform: `translateX(${-swipeOffset}px)`, offset: slideEnd },
        { transform: `translateX(${-swipeOffset}px)`, offset: slideBackStart, easing: 'ease-in-out' },
        { transform: 'translateX(0)' },
      ],
      timing,
    );
    animations.push(contentAnimation);

    if (iconElementRef.current) {
      animations.push(
        iconElementRef.current.animate(
          [{ opacity: 0 }, { opacity: 1, offset: slideEnd }, { opacity: 1, offset: slideBackStart }, { opacity: 0 }],
          timing,
        ),
      );
    }

    if (hintElementRef.current) {
      animations.push(
        hintElementRef.current.animate(
          [
            { opacity: 0 },
            { opacity: 0, offset: slideEnd },
            { opacity: 1, offset: hintVisible, easing: 'ease-in-out' },
            { opacity: 1, offset: hintHoldEnd, easing: 'ease-in-out' },
            { opacity: 0, offset: slideBackStart },
          ],
          timing,
        ),
      );
    }

    contentAnimation.onfinish = () => {
      setIsOnboardingAnimationPlaying(false);
      complete();
    };

    return () => {
      animations.forEach(animation => animation.cancel());
      setIsOnboardingAnimationPlaying(false);
    };
  }, [
    startDelayMs,
    slideMs,
    holdMs,
    textFadeMs,
    complete,
    messageRef,
    contentElementRef,
    iconElementRef,
    hintElementRef,
    isSwipeOnboardingTarget,
    onOnboardingShown,
  ]);

  return { isOnboardingAnimationPlaying };
};
